import _ from 'underscore';
import { hasReportPermission } from '../common/classes/permissions';
import { checkPermission } from '../../../behaviors/src/lib/permissions';

// unfortunately, reportType comes in two different ways, depending on
// whether or not it is passed in as the active report vs one of a list
// of the index of reports.  In the former, it is an object.  In the latter
// it is just an identifier.  So, we have to check for the difference.
const getReportTypeIdentifier = reportType => typeof reportType === 'object' && reportType.identifier ? reportType.identifier : reportType;
const hasApprovePermissionForSelections = (user, selectedReports) => {
  let hasPermission = true;
  for (let i = 0; i < selectedReports.length; i += 1) {
    const currentReport = selectedReports[i];
    const reportTypeIdentifier = getReportTypeIdentifier(currentReport.reportType);
    // if owner, must have report_approve_own permission
    if (user.user_id === currentReport.appCreatorID && !hasReportPermission(user, 'report_approve_own', reportTypeIdentifier)) {
      return false;
    }

    // check for report_approve
    if (!hasReportPermission(user, 'report_approve', reportTypeIdentifier)) {
      hasPermission = false;
      break;
    }

    // this permission disallows
    if (hasReportPermission(user, 'report_read_only')) {
      hasPermission = false;
      break;
    }
  }
  return hasPermission;
};
const hasProcessPermission = user => hasReportPermission(user, 'report_process');

// Logic outlined here:
// https://sites.google.com/a/truqcapp.com/wiki/csv-administration/user-permissions

// user has two settings for permissions - the GAP.csv file and the RTGAP.csv file.
// The GAP takes precedent, so we want to check there first.  This includes all
// report type permissions as well as general, non-specific permissions.
// GAP permissions are stored in user.permissions.general
//
// RTGAP permissions are stored in user.permissions.reportTypes.
//
// Also - some actions are only available when report has a certain status:
//
// Report Status        Available Actions
// --------------------------------------
// Draft                Submit/Approve/Reject/Delete
// Submitted            Submit/Approve/Reject
// Rejected             Submit/Approve/Reject
// Approved             Approve/Reject
// Processed            Approve/Reject
//
// These actions are disabled unless the permission is present:
//
// - Approve/Reject a Report
// - Approve/Reject his or her own Report
// - Process a Report
//
//
export default {
  isActionDisabled(user, action, selectedReports) {
    // If nothing is selected, all buttons are disabled
    if (!selectedReports || selectedReports.length === 0) {
      return true;
    }
    const selectedStatuses = selectedReports.map(({
      statusName
    }) => statusName);

    // returns false if ALL actions are denied using
    // deny_report_actions
    let isDisabled = false;
    if (checkPermission({
      resourceIdentifier: 'report'
    }, 'actions', user.permissions, user) === false) {
      // all actions are disabled.
      return true;
    }

    // this returns false IFF the action
    // is explicitly denied.
    if (checkPermission({
      resourceIdentifier: 'report'
    }, action, user.permissions, user) === false) {
      // all actions are disabled.
      return true;
    }
    switch (action) {
      case 'assign':
        if (hasReportPermission(user, 'read_only')) {
          isDisabled = true;
        }
        // this permission disallows
        if (hasReportPermission(user, 'report_read_only')) {
          isDisabled = true;
        }
        break;
      // approve and reject are the same.
      case 'approve':
      case 'reject':
        // must have permission for all reports
        if (!hasApprovePermissionForSelections(user, selectedReports)) {
          isDisabled = true;
        }
        break;
      case 'process':
        // must have report_process permission and report must be approved
        if (!hasProcessPermission(user) || _.intersection(selectedStatuses, ['Draft', 'Rejected', 'Submitted', 'Processed']).length) {
          isDisabled = true;
        }
        break;
      case 'delete':
        if (_.intersection(selectedStatuses, ['Draft', 'Rejected']).length > 0 && _.intersection(selectedStatuses, ['Approved', 'Submitted', 'Processed']).length === 0) {
          // check DRAFT and Rejected reports.
          selectedReports.map(report => report.reportType).forEach(reportType => {
            const identifier = getReportTypeIdentifier(reportType);
            if (selectedStatuses.includes('Draft')) {
              // for Draft status, they can delete if they have permission
              isDisabled = isDisabled || !hasReportPermission(user, 'report_delete_draft', identifier);
            }
            if (selectedStatuses.includes('Rejected')) {
              // for Rejected status, they can delete if they have permission
              isDisabled = isDisabled || !hasReportPermission(user, 'report_delete_rejected', identifier);
            }
          });
        } else {
          // all other statuses are disabled.
          isDisabled = true;
        }
        break;
      case 'submit':
        // cant be Approved or Processed, otherwise no other permission required
        if (_.intersection(selectedStatuses, ['Approved', 'Processed']).length) {
          isDisabled = true;
        }

        // this permission disallows
        if (hasReportPermission(user, 'report_read_only')) {
          isDisabled = true;
        }
        break;
      default:
        break;
    }
    return isDisabled;
  }
};